import React, { useEffect, useState } from "react";
import type { StockNewsItemData } from "@crypto-trading/common/browser";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import type { LivePriceExchangeCode } from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";

interface StockNewsTileProps {
  item: StockNewsItemData;
  onDetails: () => void;
  onTrade?: () => void;
}

function formatPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return "Not available";
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function excerpt(body: string, title: string): string {
  const text = body.trim();
  if (!text || text === title.trim()) return "";
  if (text.length <= 180) return text;
  return `${text.slice(0, 177).trimEnd()}...`;
}

export const StockNewsTile: React.FC<StockNewsTileProps> = ({
  item,
  onDetails,
  onTrade,
}) => {
  const matched = Boolean(item.primaryTicker);
  const [tileRef, isInView] = useInView<HTMLElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });
  const liveExchange = (item.alertPriceExchange ||
    "dynamic") as LivePriceExchangeCode;
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    matched ? item.priceSymbol : null,
    liveExchange,
    matched && isInView
  );
  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (!matched || !isInView || livePrice) {
      setPriceLoadTimeout(false);
      return;
    }
    const timer = setTimeout(() => setPriceLoadTimeout(true), 8000);
    return () => clearTimeout(timer);
  }, [matched, isInView, livePrice]);

  const priceLoading = matched && isInView && !livePrice && !priceLoadTimeout;
  const change =
    item.alertPrice && livePrice
      ? ((livePrice - item.alertPrice) / item.alertPrice) * 100
      : null;
  const description = excerpt(item.body, item.title);
  const sourceLabel = item.sourceName || item.source || "Stocks";

  return (
    <article ref={tileRef} className="glass flex flex-col gap-3 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 truncate text-xs text-[#d4d4d4]">
          <span className="font-semibold">{sourceLabel}</span>
          <span className="text-[#a3a3a3]"> · {formatTimeAgo(item.publishedAt)}</span>
        </p>
        <span className="shrink-0 rounded-full border border-[#a16207] bg-[#2a1d05] px-2 text-xs font-medium leading-5 text-[#fbbf24]">
          Stocks
        </span>
      </div>

      <div>
        <h3 className="text-sm font-semibold leading-5 text-white">{item.title}</h3>
        {description && (
          <p className="mt-1 line-clamp-2 text-xs leading-[18px] text-[#a3a3a3]">
            {description}
          </p>
        )}
      </div>

      {matched && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-lg bg-[#0a0a0a]/60 px-2 py-1">
            <span className="text-sm font-semibold text-white">{item.primaryTicker}</span>
            {priceLoading ? (
              <span className="h-4 w-12 animate-pulse rounded bg-gray-600/30" />
            ) : change != null ? (
              <span
                className={`text-sm font-medium ${change >= 0 ? "text-[#4ade80]" : "text-[#f87171]"}`}
              >
                {change >= 0 ? "+" : ""}
                {change.toFixed(2)}%
              </span>
            ) : null}
            {isSubscribed && livePrice ? (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
            ) : null}
          </span>
          <span className="text-xs text-[#a3a3a3]">
            Alert {formatPrice(item.alertPrice)} · Now {priceLoading ? "…" : formatPrice(livePrice)}
          </span>
        </div>
      )}

      {item.matches.length > 1 && (
        <p className="text-xs text-[#a3a3a3]">
          Also mentions{" "}
          {item.matches
            .map((match) => match.ticker)
            .filter((ticker) => ticker !== item.primaryTicker)
            .join(", ")}
        </p>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onDetails}
          className="flex-1 rounded-full border border-white/15 bg-[#171717] py-1.5 text-sm font-semibold text-white hover:bg-[#262626]"
        >
          Details
        </button>
        {matched && onTrade && (
          <button
            type="button"
            onClick={onTrade}
            className="flex-1 rounded-full bg-accent py-1.5 text-sm font-semibold text-white hover:bg-accent-hover"
          >
            Trade
          </button>
        )}
      </div>
    </article>
  );
};
